import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
} from '@heroicons/react/24/outline'
import Button from '@/components/common/Button'
import { clsx } from 'clsx'
import toast from 'react-hot-toast'
import type { OrchestratorState, OrchestratorPhase } from './useOrchestratorStream'

const PHASES: { key: OrchestratorPhase; label: string }[] = [
  { key: 'prefetching', label: 'Pre-fetching' },
  { key: 'decomposing', label: 'Decomposing' },
  { key: 'executing', label: 'Running' },
  { key: 'verifying', label: 'Verifying' },
  { key: 'synthesizing', label: 'Synthesizing' },
]

const ORDER: OrchestratorPhase[] = [
  'prefetching',
  'decomposing',
  'executing',
  'verifying',
  'synthesizing',
  'done',
]

function phaseIndex(p: OrchestratorPhase): number {
  const i = ORDER.indexOf(p)
  return i < 0 ? 0 : i
}

export default function OrchestratorResultPanel({ state }: { state: OrchestratorState }) {
  const navigate = useNavigate()
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null)

  const elapsedMs = useMemo(() => {
    if (!state.startedAt) return 0
    const end = state.endedAt || Date.now()
    return end - state.startedAt
  }, [state.startedAt, state.endedAt])

  const currentIdx = phaseIndex(state.phase)
  const runningTasks = state.tasks.filter((t) => t.status === 'pending' || t.status === 'running').length
  const doneTasks = state.tasks.filter((t) => t.status === 'completed').length
  const failedTasks = state.tasks.filter((t) => t.status === 'failed').length

  const copyAnswer = () => {
    if (!state.output) return
    navigator.clipboard.writeText(state.output).then(
      () => toast.success('Answer copied'),
      () => toast.error('Copy failed'),
    )
  }

  const openTrace = () => {
    if (!state.requestId) return
    navigate(`/playground?replay=${encodeURIComponent(state.requestId)}`)
  }

  return (
    <div className="rounded-lg border border-border bg-muted/30 overflow-hidden" data-testid="orchestrator-result-panel">
      {/* Status strip */}
      <div className="px-4 py-2.5 bg-card border-b border-border flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs">
          {state.phase === 'done' ? (
            <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
          ) : state.phase === 'error' ? (
            <XCircleIcon className="w-4 h-4 text-red-500" />
          ) : (
            <ClockIcon className="w-4 h-4 text-indigo-500 animate-pulse" />
          )}
          <span className="font-medium text-foreground capitalize">
            {state.phase === 'done'
              ? 'Complete'
              : state.phase === 'error'
                ? 'Error'
                : state.phase.replace(/ing$/, 'ing…')}
          </span>
        </div>

        <div className="flex items-center gap-1 flex-1 min-w-0">
          {PHASES.map((p, idx) => {
            const done = idx < currentIdx || state.phase === 'done'
            const active = p.key === state.phase && state.phase !== 'done' && state.phase !== 'error'
            return (
              <div key={p.key} className="flex items-center gap-1 flex-shrink-0">
                <div
                  className={clsx(
                    'w-2 h-2 rounded-full transition-colors',
                    done ? 'bg-emerald-500' : active ? 'bg-indigo-500 animate-pulse' : 'bg-muted-foreground/30',
                  )}
                />
                <span
                  className={clsx(
                    'text-[11px]',
                    done
                      ? 'text-muted-foreground'
                      : active
                        ? 'text-foreground font-medium'
                        : 'text-muted-foreground/60',
                  )}
                >
                  {p.label}
                </span>
                {idx < PHASES.length - 1 && (
                  <span className="w-3 h-px bg-muted-foreground/20" />
                )}
              </div>
            )
          })}
        </div>

        <div className="text-[11px] text-muted-foreground font-mono">
          {(elapsedMs / 1000).toFixed(1)}s
          {state.tasks.length > 0 && ` · ${state.tasks.length} tasks`}
          {state.groupsTotal > 0 && ` · group ${state.groupsDone}/${state.groupsTotal}`}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {state.prefetch && (
          <div className="text-xs text-muted-foreground">
            ▸ Pre-fetched <span className="text-foreground font-medium">{state.prefetch.entities ?? '?'}</span> entities
            {typeof state.prefetch.confidence === 'number' && (
              <> · confidence <span className="text-foreground font-medium">{(state.prefetch.confidence * 100).toFixed(0)}%</span></>
            )}
          </div>
        )}

        {state.tasks.length > 0 && (
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">
              ▸ Decomposed into {state.tasks.length} task{state.tasks.length === 1 ? '' : 's'}
              {' · '}
              <span className="text-emerald-600 dark:text-emerald-400">{doneTasks} done</span>
              {runningTasks > 0 && <>, <span className="text-indigo-600 dark:text-indigo-400">{runningTasks} pending</span></>}
              {failedTasks > 0 && <>, <span className="text-red-600 dark:text-red-400">{failedTasks} failed</span></>}
            </div>
            <ul className="space-y-1">
              {state.tasks.map((t) => (
                <li key={t.id} className="flex items-center gap-2 text-xs">
                  {t.status === 'completed' ? (
                    <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  ) : t.status === 'failed' ? (
                    <XCircleIcon className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                  ) : (
                    <span className={clsx(
                      'w-3 h-3 rounded-full flex-shrink-0',
                      t.status === 'running' ? 'bg-indigo-500 animate-pulse' : 'border-2 border-muted-foreground/40',
                    )} />
                  )}
                  <span className="font-mono text-muted-foreground">{t.agent_name}</span>
                  {t.description && (
                    <span className="text-muted-foreground truncate">— {t.description}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {state.verification && (
          <div className="text-xs text-muted-foreground">
            ▸ Verification:{' '}
            <span className={clsx(
              'font-medium',
              state.verification.verdict === 'PASS'
                ? 'text-emerald-600 dark:text-emerald-400'
                : state.verification.verdict === 'FAIL'
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-foreground',
            )}>
              {state.verification.verdict || 'in progress'}
            </span>
            {typeof state.verification.checks === 'number' && ` · ${state.verification.checks} checks`}
          </div>
        )}

        {state.phase === 'error' && (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400">
            {state.errorMessage || 'Orchestrator failed'}
          </div>
        )}

        {state.output && state.phase === 'done' && (
          <div className="mt-2 rounded-md bg-card border border-border p-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Synthesis</div>
            <div
              className="text-sm text-foreground whitespace-pre-wrap break-words max-h-80 overflow-y-auto"
              data-testid="orchestrator-synthesis"
            >
              {state.output}
            </div>
          </div>
        )}

        {(state.phase === 'done' || state.phase === 'error') && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {state.output && (
              <Button variant="ghost" size="sm" onClick={copyAnswer}>
                <ClipboardDocumentIcon className="w-4 h-4 mr-1" />
                Copy answer
              </Button>
            )}
            {state.requestId && (
              <Button variant="ghost" size="sm" onClick={openTrace} data-testid="orchestrator-view-trace">
                <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-1" />
                View full trace
              </Button>
            )}
            {state.phase === 'done' && (
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setFeedback('up')}
                  className={clsx(
                    'p-1.5 rounded-md hover:bg-muted transition-colors',
                    feedback === 'up' && 'bg-emerald-500/10 text-emerald-600',
                  )}
                  aria-label="Good result"
                >
                  <HandThumbUpIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setFeedback('down')}
                  className={clsx(
                    'p-1.5 rounded-md hover:bg-muted transition-colors',
                    feedback === 'down' && 'bg-red-500/10 text-red-600',
                  )}
                  aria-label="Bad result"
                >
                  <HandThumbDownIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
